<script setup lang="ts">
import { IClientPaymentPlanParams } from "@/models/plan";
import {
  IonButton,
  IonGrid,
  IonRow,
  IonCol,
  IonCard,
  IonCardContent,
  IonProgressBar,
  IonSkeletonText,
  IonText,
  useIonRouter,
  onIonViewWillEnter,
} from "@ionic/vue";
import { onBeforeMount, reactive, watch } from "vue";
import { useUserStore } from "@/stores/user";
import { usePlansStore } from "@/stores/plans";
import { getSubscribedPlans } from "@/api/plans";
import { getClientAccount } from "@/api/clients";

const userStore = useUserStore();
const plansStore = usePlansStore();
const router = useIonRouter();

// const urlSubscriptionsFrontend = process.env.VUE_APP_SUBSCRIPTIONS_SERVER_PATH;
const state = reactive({
  loading: false,
  errors: {
    name: [],
    phone_number: [],
  },
  current_plan: {},
  current_billing_cicle: "",
  subscribed_plans: Array<IClientPaymentPlanParams>,
  next_charge_at: "",
  client_account: {},
  total_visitors: 0,
  successfull_purchase: false,
  revenue_plans: [],
});

onIonViewWillEnter(async () => {
  await loadPlanInformation();
});

onBeforeMount(async () => {
  await loadPlanInformation();
});
const loadPlanInformation = async () => {
  userStore.fetchRoles();
  await plansStore.fetch({ reset: true, page_size: 10 });
  // state.current_plan = plansStore.results[0];

  const plan = await getSubscribedPlans();
  state.subscribed_plans = plan.data.results;

  let current_plan_id = 0;

  state.subscribed_plans.forEach((element: any) => {
    if (element.subscription_status === "active") {
      state.current_billing_cicle = element.billing_cycle;
      state.next_charge_at = element.subscription_current_period_end;
      current_plan_id = element.payment_plan;
      return;
    }
  });

  // Get plan information
  const current_plan = await plansStore.fetchPlan(current_plan_id);
  state.current_plan = current_plan.result;
};

watch(
  () => userStore.related.clients,
  async (clients) => {
    if (userStore.related.clients.length) {
      const client_account = await getClientAccount(clients[0].id);
      state.client_account = client_account.data;
    }
  }
);

const getProgressResult = (existing: number, allowed: number) => {
  return existing / allowed;
};

const isValidNumber = (value: any) => {
  return typeof value === "number";
};

const openMySubscription = () => {
  router.push("/my-subscription");
};
</script>

<template>
  <ion-card class="ion-margin">
    <ion-grid>
      <ion-row class="ion-justify-content-end">
        <ion-col class="ion-margin-start">
          <ion-text color="dark">
            <h4
              class="plan_name ion-text-lowercase"
              v-if="state.current_plan.name"
            >
              {{ state.current_plan.name }}
            </h4>
            <h4 v-else>
              <ion-skeleton-text
                :animated="true"
                class="billing_cicle_skeleton"
              ></ion-skeleton-text>
            </h4>
          </ion-text>
          <ion-text
            v-if="state.next_charge_at && !state.current_plan.is_a_free_plan"
            color="dark"
            class="font-weight-400"
            >Next charge at: {{ state.next_charge_at }}</ion-text
          >
          <br />
          <ion-text
            v-if="
              state.current_billing_cicle && !state.current_plan.is_a_free_plan
            "
            color="dark"
            class="font-weight-400"
            ><span class="ion-text-capitalize">{{
              state.current_billing_cicle
            }}</span>
            subscription</ion-text
          >
        </ion-col>
        <ion-col class="freeplan" size="3">
          <ion-button
            v-if="state.current_plan.is_a_free_plan"
            size="large"
            fill="clear"
            color="medium"
            >FREE</ion-button
          >
          <!-- Monthly information EUR -->
          <ion-button
            v-if="
              !state.current_plan.is_a_free_plan &&
              state.current_billing_cicle === 'monthly' &&
              state.current_plan.monthly_price_eur > 0
            "
            size="large"
            fill="clear"
            color="dark"
            >€{{ state.current_plan.monthly_price_eur / 100 }}</ion-button
          >
          <!-- Annually information  EUR -->
          <ion-button
            v-if="
              !state.current_plan.is_a_free_plan &&
              state.current_billing_cicle === 'annually' &&
              state.current_plan.annual_price_eur > 0
            "
            size="large"
            fill="clear"
            color="dark"
            >€{{ state.current_plan.annual_price_eur / 100 }}</ion-button
          >
        </ion-col>
      </ion-row>
    </ion-grid>

    <ion-card-content>
      <label v-if="state.client_account.owned_events">
        {{ state.client_account.owned_events?.length }} of
        {{ state.current_plan.check_in_max_events }} Events
      </label>
      <ion-skeleton-text
        :animated="true"
        style="width: 50%"
        v-else
      ></ion-skeleton-text>
      <ion-progress-bar
        v-if="state.client_account.owned_events"
        :value="
          getProgressResult(
            state.client_account.owned_events?.length,
            state.current_plan?.check_in_max_events
          )
        "
        class="ion-margin-top ion-margin-bottom"
      ></ion-progress-bar>
      <ion-skeleton-text
        class="skeleton_limits"
        :animated="true"
        v-else
      ></ion-skeleton-text>

      <label v-if="isValidNumber(state.client_account.allocated_capacity)">
        {{ state.client_account.allocated_capacity }} of
        {{ state.current_plan.check_in_max_capacity }} Visitors
      </label>
      <ion-skeleton-text
        :animated="true"
        style="width: 50%"
        v-else
      ></ion-skeleton-text>
      <ion-progress-bar
        v-if="isValidNumber(state.client_account.allocated_capacity)"
        :value="
          getProgressResult(
            state.client_account.allocated_capacity,
            state.current_plan.check_in_max_capacity
          )
        "
        class="ion-margin-top ion-margin-bottom"
      ></ion-progress-bar>
      <ion-skeleton-text
        class="skeleton_limits"
        :animated="true"
        v-else
      ></ion-skeleton-text>

      <label v-if="isValidNumber(state.client_account.event_managers)">
        {{ state.client_account.event_managers }} of
        {{ state.current_plan.check_in_max_event_managers }} Event Managers
      </label>
      <ion-skeleton-text
        :animated="true"
        style="width: 50%"
        v-else
      ></ion-skeleton-text>
      <ion-progress-bar
        v-if="isValidNumber(state.client_account.event_managers)"
        :value="
          getProgressResult(
            state.client_account.event_managers,
            state.current_plan.check_in_max_event_managers
          )
        "
        class="ion-margin-top ion-margin-bottom"
      ></ion-progress-bar>
      <ion-skeleton-text
        class="skeleton_limits"
        :animated="true"
        v-else
      ></ion-skeleton-text>

      <ion-grid>
        <ion-row class="ion-justify-content-center">
          <ion-button
            color="primary"
            class="ion-margin-top"
            @click="openMySubscription()"
          >
            MANAGE PLAN
          </ion-button>
        </ion-row>
      </ion-grid>
    </ion-card-content>
  </ion-card>
</template>

<style lang="scss" scoped>
ion-card {
  border-radius: 10px;
  border: 1px solid;
}
.freeplan {
  padding: 0px;
  margin-right: 2px;
}

.plan_name {
  font-weight: bold;
}

.font-weight-400 {
  font-weight: 400;
  color: black;
}

.manage_account_btn {
  text-decoration: none;
  color: white;
}

@media (min-width: 768px) {
  .freeplan {
    text-align: right;
  }
}
.plans {
  text-transform: uppercase;
}
</style>
